import React, { useState, useEffect } from "react";
import { db } from "../../config/firebaseConfig";
import { ref, get } from "firebase/database";
import { Skeleton } from "../../@/components/ui/skeleton";

interface CredentialSection {
  title: string;
  content: string;
}

interface CredentialsData {
  introduction: string;
  sections: CredentialSection[];
}

const CredentialsSkeleton = () => (
  <div className="p-4 md:p-4">
    <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5">
      <div>
        <Skeleton className="mt-[4%] h-16 w-3/4" />
      </div>

      <Skeleton className="h-20 w-full" />

      <div className="space-y-6">
        {[1, 2, 3, 4, 5, 6].map((index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Credentials: React.FC = () => {
  const [credentialsData, setCredentialsData] = useState<CredentialsData>({
    introduction: "",
    sections: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }

    // Fetch credentials data from Firebase
    const fetchCredentialsData = async () => {
      try {
        const credentialsRef = ref(db, "LayoutSettingData/Credentials");
        const snapshot = await get(credentialsRef);

        if (snapshot.exists()) {
          setCredentialsData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: CredentialsData = {
            introduction:
              "Your login credentials are a vital part of protecting your account. Here's how we manage and secure your credentials.",
            sections: [
              {
                title: "Account Information",
                content:
                  "When you create an account on our platform, you will be required to choose a unique username and password. This information must be kept confidential, and you are responsible for all activities that occur under your account.",
              },
              {
                title: "Password Security",
                content:
                  "We recommend using a strong password that contains at least eight characters, including uppercase letters, lowercase letters, numbers, and symbols. Avoid using easily guessable information such as birthdays or common phrases.",
              },
              {
                title: "Two-Factor Authentication (2FA)",
                content:
                  "To enhance your account security, we offer two-factor authentication (2FA). By enabling 2FA, you will be required to enter a one-time passcode (OTP) sent to your phone or email in addition to your password when logging in.",
              },
              {
                title: "Forgotten Credentials",
                content:
                  'If you forget your password, you can reset it by using the "Forgot Password" option on the login page. A password reset link will be sent to your registered email address.',
              },
              {
                title: "Updating Credentials",
                content:
                  "You can update your password or email address at any time by going to the account settings page. We recommend updating your password regularly for enhanced security.",
              },
              {
                title: "Contact Us",
                content:
                  "If you have any concerns about your account security or believe your credentials have been compromised, please contact our support team immediately at admin@afrocart.ca.",
              },
            ],
          };
          setCredentialsData(defaultData);
        }
      } catch (error) {
        console.error("Error fetching credentials data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCredentialsData();
  }, []);

  // Helper function to process email links in content
  const renderContent = (content: string) => {
    // Check if content contains email addresses
    if (content.includes("@")) {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g;
      const parts = content.split(emailRegex);

      return (
        <>
          {parts.map((part, index) => {
            if (part.match(emailRegex)) {
              return (
                <a
                  key={index}
                  href={`mailto:${part}`}
                  className="font-helveticaMedium text-customGreenColor opacity-50"
                >
                  {part}
                </a>
              );
            }
            return part;
          })}
        </>
      );
    }

    return content;
  };

  if (loading) {
    return <CredentialsSkeleton />;
  }

  return (
    <div>
      <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5 text-black">
        <div>
          {/* <p className="flex items-start text-[1.65vh] text-customGrayBase">
            Last modified: 10/22/2024
          </p> */}
          <h1 className="mt-[4%] font-helveticaBold text-[5.5vh] xxl:text-[7.5vh] leading-none text-black">
            Account Credentials
          </h1>
        </div>

        <p className="font-helvetica">{credentialsData.introduction}</p>

        <ol className="decimal_ol space-y-4 px-4">
          {credentialsData.sections.map((section, index) => (
            <li key={index} className="font-helveticaBold text-black">
              {section.title}
              <p className="font-helvetica text-black">
                {renderContent(section.content)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default Credentials;
